/**
 * Deployment-specific overrides for the compiled environment.
 *
 * Every URL this app calls is compiled in from src/environments at build
 * time. A deployment that serves runtime-config.json beside index.html can
 * replace those values without a rebuild; the fleet renders one per
 * environment. Anywhere that does not serve it (existing servers, native
 * builds, ng serve) gets a 404, or index.html from the SPA fallback, and the
 * compiled values are kept unchanged.
 *
 * Only the keys the caller lists are replaced, and only when both the
 * compiled and the supplied value are strings, so the file can repoint a
 * URL but cannot change any other setting.
 *
 * Startup waits for this, so the request has a hard deadline: a server
 * that accepts it and never answers costs at most that long, after which
 * the compiled values are used.
 */
export const RUNTIME_CONFIG_PATH = 'runtime-config.json';

/** Ample for a few hundred bytes from the same origin, even on a slow link. */
export const RUNTIME_CONFIG_TIMEOUT_MS = 5000;

export type RuntimeConfigFetch = (input: string, init?: RequestInit) => Promise<Response>;

export interface RuntimeConfigOptions {
    fetchFn?: RuntimeConfigFetch;
    timeoutMs?: number;
}

export async function applyRuntimeConfig(
    target: object,
    keys: readonly string[],
    options: RuntimeConfigOptions = {}
): Promise<string[]> {
    const overrides = await readOverrides(
        options.fetchFn || ((input, init) => fetch(input, init)),
        options.timeoutMs === undefined ? RUNTIME_CONFIG_TIMEOUT_MS : options.timeoutMs
    );
    if (overrides === null) {
        return [];
    }
    const settings = target as Record<string, unknown>;
    const applied: string[] = [];
    for (const key of keys) {
        if (
            hasOwn(overrides, key)
            && hasOwn(settings, key)
            && typeof settings[key] === 'string'
            && typeof overrides[key] === 'string'
        ) {
            settings[key] = overrides[key];
            applied.push(key);
        }
    }
    return applied;
}

async function readOverrides(
    fetchFn: RuntimeConfigFetch,
    timeoutMs: number
): Promise<Record<string, unknown> | null> {
    const controller = typeof AbortController === 'function' ? new AbortController() : undefined;
    let timer: ReturnType<typeof setTimeout> | undefined;
    // Covers reading the body as well as the response headers.
    const deadline = new Promise<null>((resolve) => {
        timer = setTimeout(() => {
            if (controller) {
                controller.abort();
            }
            resolve(null);
        }, timeoutMs);
    });
    const request = (async (): Promise<Record<string, unknown> | null> => {
        try {
            const response = await fetchFn(RUNTIME_CONFIG_PATH, {
                cache: 'no-store',
                signal: controller ? controller.signal : undefined
            });
            if (!response.ok) {
                return null;
            }
            const body: unknown = await response.json();
            return body !== null && typeof body === 'object' && !Array.isArray(body)
                ? body as Record<string, unknown>
                : null;
        } catch {
            return null;
        }
    })();
    try {
        return await Promise.race([request, deadline]);
    } finally {
        clearTimeout(timer);
    }
}

function hasOwn(value: object, key: string): boolean {
    return Object.prototype.hasOwnProperty.call(value, key);
}
